import React, { useState, useEffect, useRef, useMemo } from "react";
import { useApp } from "../../contexts/AppContext";
import { apiService } from "../../services/api";
import { Shop, RouteEntity, Category } from "../../types";
import styles from "./TabContent.module.css";
import { useLocation } from "react-router-dom";
import RouteQRModal from "../RouteQRModal/RouteQRModal";
import { getSocialMediaIcon, formatSocialMediaUrl } from "./socialMediaUtils";

interface ShopTabContentProps {
  activeTabId: string | null;
  tabName: string;
  onTabChange?: (tabId: string | null) => void;
  tabIdNumber: number | null;
  currentTab: any; // Adjust type as needed
}

export default function ShopTabContent({
  activeTabId,
  tabName,
  onTabChange,
  tabIdNumber,
  currentTab,
}: ShopTabContentProps) {
  const {
    state,
    setSelectedShop,
    setCurrentFloor,
    setActiveRoutePoints,
    setSelectedShopReload,
    setTerminalName,
    setCurrentFloorReload,
  } = useApp();
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedShop, setSelectedShopLocal] = useState<Shop | null>(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
    new Set(["all"]),
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const routerLocation = useLocation();
  const shopThumbnailsRef = useRef<HTMLDivElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const categoryFilterRef = useRef<HTMLDivElement>(null);

  // Reset selected shop when tab changes
  useEffect(() => {
    setSelectedShopLocal(null);
    setCurrentPhotoIndex(0);
    setSelectedCategories(new Set(["all"]));
    setIsDropdownOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);

  // When switching to a shop's tab via "More details" button, load and show the shop
  useEffect(() => {
    const loadShopFromState = async () => {
      if (!activeTabId || tabIdNumber === 1) {
        return;
      }

      if (
        selectedShop &&
        state.selectedShop &&
        selectedShop.id === state.selectedShop.id
      ) {
        const shopTabId = state.selectedShop.tabId
          ? String(state.selectedShop.tabId)
          : selectedShop.tabId
            ? String(selectedShop.tabId)
            : null;

        if (shopTabId && shopTabId !== activeTabId) {
          setSelectedShopLocal(null);
        }
        return;
      }

      if (state.selectedShop) {
        const shopTabId = state.selectedShop.tabId
          ? String(state.selectedShop.tabId)
          : null;

        if (shopTabId && shopTabId === activeTabId) {
          if (!selectedShop || selectedShop.id !== state.selectedShop.id) {
            try {
              const shop = await apiService.getShopById(state.selectedShop.id);
              setSelectedShopLocal(shop);
              setCurrentPhotoIndex(0);
            } catch (e) {
              console.error("Error loading shop details:", e);
            }
          }
        } else if (shopTabId && shopTabId !== activeTabId) {
          setSelectedShopLocal(null);
        } else {
          const shopInCurrentTab = shops.find(
            (s) => s.id === state.selectedShop?.id,
          );
          if (shopInCurrentTab) {
            if (!selectedShop || selectedShop.id !== state.selectedShop.id) {
              setSelectedShopLocal(state.selectedShop);
              setCurrentPhotoIndex(0);
            }
          } else {
            setSelectedShopLocal(null);
          }
        }
      } else {
        if (selectedShop && activeTabId) {
          return;
        }
        setSelectedShopLocal(null);
      }
    };

    loadShopFromState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId, state.selectedShop]);

  // Load categories for current tab
  useEffect(() => {
    if (!activeTabId || tabIdNumber === 1) {
      setCategories([]);
      return;
    }

    const loadCategories = async () => {
      try {
        const tabIdNum = parseInt(activeTabId);
        const allCategories = await apiService.getCategories(tabIdNum);
        setCategories(allCategories);
      } catch (error) {
        console.error("Error loading categories:", error);
        setCategories([]);
      }
    };

    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);

  // Reload categories and shops when tabs are updated (e.g., after deletion)
  useEffect(() => {
    const handleTabsUpdate = () => {
      if (!activeTabId || tabIdNumber === 1) {
        setCategories([]);
        setShops([]);
        return;
      }

      // Reload categories
      const loadCategories = async () => {
        try {
          const tabIdNum = parseInt(activeTabId);
          const allCategories = await apiService.getCategories(tabIdNum);
          setCategories(allCategories);
        } catch (error) {
          console.error("Error reloading categories:", error);
          setCategories([]);
        }
      };

      // Reload shops
      loadShops();
      loadCategories();
    };

    window.addEventListener("tabsUpdated", handleTabsUpdate);
    return () => {
      window.removeEventListener("tabsUpdated", handleTabsUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId, tabIdNumber]);

  // Convert selectedCategories Set to a stable string for dependency comparison
  const selectedCategoriesKey = useMemo(() => {
    return Array.from(selectedCategories).sort().join(",");
  }, [selectedCategories]);

  // Load shops
  useEffect(() => {
    if (!activeTabId || tabIdNumber === 1) {
      setShops([]);
      return;
    }

    loadShops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId, state.currentFloor, selectedCategoriesKey, state.language]);

  const loadShops = async () => {
    if (!tabIdNumber) return;

    setLoading(true);
    try {
      let filteredShops: Shop[] = [];
      const hasCategoryFilter =
        selectedCategories.size > 0 && !selectedCategories.has("all");

      if (hasCategoryFilter) {
        const categoryIds = Array.from(selectedCategories);
        const shopPromises = categoryIds.map((categoryId) =>
          apiService.getShops({
            tabId: currentTab?.id || undefined,
            category: categoryId,
          }),
        );
        const shopArrays = await Promise.all(shopPromises);
        const shopMap = new Map<string, Shop>();
        shopArrays.forEach((shopArray) => {
          shopArray.forEach((shop) => {
            shopMap.set(shop.id, shop);
          });
        });
        filteredShops = Array.from(shopMap.values());
      } else {
        filteredShops = await apiService.getShops({
          tabId: currentTab?.id || undefined,
        });
      }

      filteredShops.sort((a, b) => {
        const nameA =
          state.language === "en"
            ? (a.nameEn && a.nameEn.trim() !== ""
                ? a.nameEn
                : a.nameRu || ""
              ).toLowerCase()
            : (a.nameRu || "").toLowerCase();
        const nameB =
          state.language === "en"
            ? (b.nameEn && b.nameEn.trim() !== ""
                ? b.nameEn
                : b.nameRu || ""
              ).toLowerCase()
            : (b.nameRu || "").toLowerCase();
        return nameA.localeCompare(
          nameB,
          state.language === "en" ? "en" : "ru",
        );
      });

      setShops(filteredShops);
    } catch (error) {
      console.error("Error loading shops:", error);
      setShops([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle horizontal scroll with mouse wheel for shop thumbnails
  useEffect(() => {
    const container = shopThumbnailsRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      if (container.scrollWidth > container.clientWidth) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [selectedShop]);

  // Handle category filter change
  const handleCategoryToggle = (categoryId: string, newChecked: boolean) => {
    setSelectedCategories((prev) => {
      if (categoryId === "all") {
        return new Set(["all"]);
      } else {
        const newSet = new Set(prev);
        newSet.delete("all");

        if (newChecked) {
          newSet.add(categoryId);
        } else {
          newSet.delete(categoryId);
          if (newSet.size === 0) {
            return new Set(["all"]);
          }
        }

        return newSet;
      }
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (
        target.tagName === "INPUT" ||
        target.tagName === "LABEL" ||
        target.closest("label")
      ) {
        return;
      }

      if (
        categoryFilterRef.current &&
        !categoryFilterRef.current.contains(target) &&
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("click", handleClickOutside, true);
      return () => {
        document.removeEventListener("click", handleClickOutside, true);
      };
    }
  }, [isDropdownOpen]);

  // Get display text for selected categories
  const getSelectedText = () => {
    if (selectedCategories.has("all") || selectedCategories.size === 0) {
      return state.language === "en" ? "All" : "Все";
    }
    if (selectedCategories.size === 1) {
      const categoryId = Array.from(selectedCategories)[0];
      const category = categories.find((c) => c.id === categoryId);
      if (category) {
        return state.language === "en"
          ? category.nameEn && category.nameEn.trim() !== ""
            ? category.nameEn
            : category.nameRu
          : category.nameRu;
      }
    }
    return `${selectedCategories.size} ${
      state.language === "en" ? "selected" : "выбрано"
    }`;
  };

  // Handle go to catalog (back to grid)
  const handleGoToCatalog = () => {
    setSelectedShopLocal(null);
    setCurrentPhotoIndex(0);
  };

  // Handle go to map
  const handleGoToMap = () => {
    const mapTab = state.tabs?.find((tab) => parseInt(tab.id) === 1);
    if (mapTab && onTabChange) {
      onTabChange(mapTab.id);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className={styles.contentContainer}>
        <div className={styles.loadingState}>
          {state.language === "en" ? "Loading..." : "Загрузка..."}
        </div>
      </div>
    );
  }

  // Show shop detail if a shop is selected
  if (selectedShop) {
    const photos = selectedShop.photos || [];
    const currentPhoto = photos.length > 0 ? photos[currentPhotoIndex] : null;
    const shopName =
      state.language === "en"
        ? selectedShop.nameEn || selectedShop.nameRu
        : selectedShop.nameRu;
    const fullDescription =
      state.language === "en"
        ? selectedShop.fullDescriptionEn || selectedShop.fullDescriptionRu
        : selectedShop.fullDescriptionRu;
    const location =
      state.language === "en"
        ? selectedShop.locationEn || selectedShop.locationRu
        : selectedShop.locationRu;
    const workingHours =
      state.language === "en"
        ? selectedShop.workingHoursEn || selectedShop.workingHoursRu
        : selectedShop.workingHoursRu;

    return (
      <div className={styles.contentContainer}>
        <div className={styles.shopDetailContainer}>
          <div className={styles.shopDetailTop}>
            <div className={styles.shopHeaderInfo}>
              <div className={styles.infoRow}>
                <div>
                  {selectedShop.logoUrl && (
                    <img
                      className={styles.shopDetailLogo}
                      src={`${process.env.REACT_APP_MEDIA_PATH}${selectedShop.logoUrl}`}
                      alt={shopName}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect width='120' height='120' fill='%23f0f0f0'/%3E%3C/svg%3E";
                      }}
                    />
                  )}
                </div>

                {
                  <div className={styles.infoItemRow}>
                    <span className={styles.infoValue}>
                      {selectedShop.floor}{" "}
                      {state.language === "en" ? "floor" : "этаж"}
                    </span>
                    <span className={styles.infoValue}>{location}</span>
                  </div>
                }

                {workingHours && (
                  <div className={styles.infoItem}>
                    <span className={styles.infoWorkingHours}>
                      {workingHours}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={styles.shopDetailRow}>
            <div className={styles.shopDetailLeft}>
              <h1 className={styles.shopDetailName}>{shopName}</h1>

              {fullDescription && (
                <p className={styles.shopDetailDescription}>
                  {fullDescription}
                </p>
              )}

              <div className={styles.actionButtons}>
                <button
                  className={`${styles.actionButton} ${styles.actionButtonPrimary}`}
                  onClick={async () => {
                    if (!selectedShop) return;
                    setSelectedShop(selectedShop);
                    const params = new URLSearchParams(routerLocation.search);
                    const terminalName =
                      params.get("terminalName") || state.terminalName;
                    try {
                      const terminals = await apiService.getTerminals({
                        active: true,
                      });
                      const terminal = terminalName
                        ? terminals.find((t) => t.name === terminalName)
                        : undefined;
                      if (!terminal) {
                        setActiveRoutePoints(undefined);
                        return;
                      }
                      const routes: RouteEntity[] =
                        await apiService.getRoutes();
                      const match = routes.find(
                        (r) =>
                          Number(r.shopId) === Number(selectedShop.id) &&
                          Number(r.terminalId) === Number(terminal.id),
                      );
                      if (match && match.points && match.points.length > 0) {
                        setSelectedShopReload(selectedShop);
                        setTerminalName(terminalName || undefined);
                        setCurrentFloorReload(terminal.floor);
                        if (state.currentFloor !== terminal.floor) {
                          setCurrentFloor(terminal.floor);
                        }
                        setTimeout(() => {
                          setActiveRoutePoints(match.points);
                          window.dispatchEvent(
                            new CustomEvent("routeRecenterRequested", {
                              detail: { terminalName },
                            }),
                          );
                        }, 50);
                        const mapTab = state.tabs?.find(
                          (tab) => parseInt(tab.id) === 1,
                        );
                        if (mapTab && onTabChange) {
                          onTabChange(mapTab.id);
                        }
                      } else {
                        setActiveRoutePoints(undefined);
                      }
                    } catch (e) {
                      console.error("Error building route:", e);
                      setActiveRoutePoints(undefined);
                    }
                  }}
                >
                  {state.language === "en"
                    ? "Build Route"
                    : "Построить маршрут"}
                </button>
                <button
                  className={styles.actionButton}
                  onClick={handleGoToCatalog}
                >
                  {state.language === "en"
                    ? "Go to Catalog"
                    : "Вернуться в каталог"}
                </button>
                <button className={styles.actionButton} onClick={handleGoToMap}>
                  {state.language === "en" ? "Go to Map" : "Перейти на карту"}
                </button>
              </div>
            </div>
            <div className={styles.shopDetailRight}>
              {photos.length > 0 && currentPhoto && (
                <div className={styles.imageSlider}>
                  <div className={styles.imageSliderContainer}>
                    {photos.length > 1 && (
                      <button
                        className={styles.sliderButton}
                        onClick={() => {
                          setCurrentPhotoIndex(
                            currentPhotoIndex > 0
                              ? currentPhotoIndex - 1
                              : photos.length - 1,
                          );
                        }}
                        aria-label={
                          state.language === "en"
                            ? "Previous photo"
                            : "Предыдущее фото"
                        }
                      >
                        ‹
                      </button>
                    )}
                    <img
                      className={styles.mainImage}
                      src={`${process.env.REACT_APP_MEDIA_PATH}${currentPhoto.url}`}
                      alt={shopName}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='400'%3E%3Crect width='800' height='400' fill='%23f0f0f0'/%3E%3C/svg%3E";
                      }}
                    />
                    {photos.length > 1 && (
                      <button
                        className={styles.sliderButton}
                        onClick={() => {
                          setCurrentPhotoIndex(
                            currentPhotoIndex < photos.length - 1
                              ? currentPhotoIndex + 1
                              : 0,
                          );
                        }}
                        aria-label={
                          state.language === "en"
                            ? "Next photo"
                            : "Следующее фото"
                        }
                      >
                        ›
                      </button>
                    )}
                  </div>
                  {photos.length > 1 && (
                    <div className={styles.imageSliderInfo}>
                      <span className={styles.imageSliderCounter}>
                        {currentPhotoIndex + 1} / {photos.length}
                      </span>
                    </div>
                  )}

                  {photos.length > 1 && (
                    <div
                      className={styles.imageThumbnails}
                      ref={shopThumbnailsRef}
                    >
                      {photos.map((photo, index) => (
                        <img
                          key={photo.id}
                          className={`${styles.thumbnail} ${
                            index === currentPhotoIndex
                              ? styles.thumbnailActive
                              : ""
                          }`}
                          src={`${process.env.REACT_APP_MEDIA_PATH}${photo.url}`}
                          alt={`${shopName} - Photo ${index + 1}`}
                          onClick={() => setCurrentPhotoIndex(index)}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect width='80' height='80' fill='%23f0f0f0'/%3E%3C/svg%3E";
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {selectedShop.socialMedia && (
          <div className={styles.socialMedia}>
            {Object.entries(selectedShop.socialMedia)
              .filter(([, url]) => url && typeof url === "string")
              .map(([platform, url]) => {
                if (!url || typeof url !== "string") return null;
                return (
                  <a key={platform} className={styles.socialIcon}>
                    {getSocialMediaIcon(platform, selectedShop.id)}
                    {formatSocialMediaUrl(url)}
                  </a>
                );
              })}
          </div>
        )}
      </div>
    );
  }

  // Show shops grid
  return (
    <div className={styles.contentContainer}>
      <div className={styles.headerSection}>
        <h2 className={styles.contentTitle}>{tabName}</h2>
        {categories.length > 0 && (
          <div className={styles.categoryFilter} ref={categoryFilterRef}>
            <div
              className={styles.categorySelect}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span className={styles.categorySelectText}>
                {getSelectedText()}
              </span>
              <div className={styles.categorySelectIcons}>
                {!selectedCategories.has("all") &&
                  selectedCategories.size > 0 && (
                    <span
                      className={
                        styles.categoryClearIcon +
                        " " +
                        styles.categorySelectIcons
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategories(new Set(["all"]));
                      }}
                    >
                      ×
                    </span>
                  )}
                <span
                  className={`${styles.categoryArrowIcon} ${
                    isDropdownOpen ? styles.categoryArrowIconOpen : ""
                  }`}
                >
                  ⌵
                </span>
              </div>
            </div>
            {isDropdownOpen && (
              <div
                ref={categoryDropdownRef}
                className={styles.categoryDropdown}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (
                    target.tagName !== "INPUT" &&
                    target.tagName !== "LABEL" &&
                    !target.closest("label")
                  ) {
                    e.stopPropagation();
                  }
                }}
              >
                <label className={styles.categoryOption}>
                  <input
                    type="checkbox"
                    checked={selectedCategories.has("all")}
                    onChange={(e) => {
                      handleCategoryToggle("all", e.target.checked);
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      const currentChecked = selectedCategories.has("all");
                      const newChecked = !currentChecked;
                      handleCategoryToggle("all", newChecked);
                    }}
                  />
                  <span>{state.language === "en" ? "All" : "Все"}</span>
                </label>
                {categories.map((category) => {
                  const categoryName =
                    state.language === "en"
                      ? category.nameEn && category.nameEn.trim() !== ""
                        ? category.nameEn
                        : category.nameRu
                      : category.nameRu;
                  const isChecked = selectedCategories.has(category.id);
                  return (
                    <label key={category.id} className={styles.categoryOption}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          handleCategoryToggle(category.id, e.target.checked);
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          const newChecked = !isChecked;
                          handleCategoryToggle(category.id, newChecked);
                        }}
                      />
                      <span>{categoryName}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {shops.length === 0 ? (
        <div className={styles.emptyState}>
          {state.language === "en" ? "No shops found" : "Объекты не найдены"}
        </div>
      ) : (
        <div className={styles.shopsGrid}>
          {shops.map((shop) => (
            <div
              key={shop.id}
              className={styles.shopCard}
              onClick={() => {
                const shopWithTabId = {
                  ...shop,
                  tabId: tabIdNumber || shop.tabId,
                };
                setSelectedShopLocal(shopWithTabId);
                setSelectedShop(shopWithTabId);
                setCurrentPhotoIndex(0);
              }}
            >
              {shop.logoUrl ? (
                <img
                  className={styles.shopLogo}
                  src={`${process.env.REACT_APP_MEDIA_PATH}${shop.logoUrl}`}
                  alt={
                    state.language === "en"
                      ? shop.nameEn || shop.nameRu
                      : shop.nameRu
                  }
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      // "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E%3C/svg%3E";
                      "data:image/svg+xml,%3Csvg width='348' height='210' viewBox='0 0 348 210' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='348' height='210' fill='white'/%3E%3Cpath d='M120.405 46.0949C120.056 45.7462 119.642 45.4696 119.187 45.2809C118.731 45.0922 118.243 44.9951 117.75 44.9951C117.257 44.9951 116.769 45.0922 116.313 45.2809C115.857 45.4696 115.444 45.7462 115.095 46.0949C114.746 46.4435 114.47 46.8574 114.281 47.313C114.092 47.7685 113.995 48.2568 113.995 48.7499C113.995 49.2429 114.092 49.7312 114.281 50.1867C114.47 50.6423 114.746 51.0562 115.095 51.4049L125.662 61.9649C122.946 65.7678 121.49 70.3265 121.5 74.9999V135C121.5 140.967 123.87 146.69 128.09 150.91C132.31 155.129 138.032 157.5 144 157.5H204C208.673 157.509 213.232 156.054 217.035 153.337L227.595 163.905C228.299 164.609 229.254 165.005 230.25 165.005C231.246 165.005 232.201 164.609 232.905 163.905C233.609 163.201 234.005 162.246 234.005 161.25C234.005 160.254 233.609 159.299 232.905 158.595L120.405 46.0949ZM169.605 105.907C168.306 106.459 167.123 107.251 166.117 108.24L131.092 142.65C129.721 140.334 128.998 137.691 129 135V74.9999C129 72.2249 129.757 69.6149 131.077 67.3799L169.605 105.907ZM144 150C141.225 150 138.637 149.25 136.41 147.945L171.375 113.595C172.076 112.908 173.018 112.523 174 112.523C174.981 112.523 175.924 112.908 176.625 113.595L211.59 147.945C209.288 149.293 206.668 150.002 204 150H144ZM219 74.9999V134.092L225.735 140.827C226.245 138.962 226.5 137.02 226.5 135V74.9999C226.5 69.0325 224.129 63.3095 219.91 59.09C215.69 54.8704 209.967 52.4999 204 52.4999H144C141.975 52.4999 140.032 52.7549 138.172 53.2649L144.9 59.9999H204C207.978 59.9999 211.793 61.5802 214.606 64.3933C217.42 67.2063 219 71.0216 219 74.9999ZM204 86.2499C204 84.7725 203.709 83.3096 203.144 81.9447C202.578 80.5798 201.749 79.3396 200.705 78.2949C199.66 77.2502 198.42 76.4216 197.055 75.8562C195.69 75.2908 194.227 74.9999 192.75 74.9999C191.272 74.9999 189.81 75.2908 188.445 75.8562C187.08 76.4216 185.84 77.2502 184.795 78.2949C183.75 79.3396 182.922 80.5798 182.356 81.9447C181.791 83.3096 181.5 84.7725 181.5 86.2499C181.5 89.2335 182.685 92.095 184.795 94.2048C186.905 96.3146 189.766 97.4999 192.75 97.4999C195.734 97.4999 198.595 96.3146 200.705 94.2048C202.815 92.095 204 89.2335 204 86.2499ZM196.5 86.2499C196.5 87.2444 196.105 88.1982 195.402 88.9015C194.698 89.6048 193.744 89.9999 192.75 89.9999C191.755 89.9999 190.801 89.6048 190.098 88.9015C189.395 88.1982 189 87.2444 189 86.2499C189 85.2553 189.395 84.3015 190.098 83.5982C190.801 82.8949 191.755 82.4999 192.75 82.4999C193.744 82.4999 194.698 82.8949 195.402 83.5982C196.105 84.3015 196.5 85.2553 196.5 86.2499Z' fill='url(%23paint0_linear_5310_528)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_5310_528' x1='174' y1='44.9951' x2='174' y2='165.005' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2364A696'/%3E%3Cstop offset='0.5' stop-color='%2364A696'/%3E%3Cstop offset='1' stop-color='%2364A696'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E";
                  }}
                />
              ) : (
                <img
                  className={styles.shopLogo}
                  src="data:image/svg+xml,%3Csvg width='348' height='210' viewBox='0 0 348 210' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='348' height='210' fill='white'/%3E%3Cpath d='M120.405 46.0949C120.056 45.7462 119.642 45.4696 119.187 45.2809C118.731 45.0922 118.243 44.9951 117.75 44.9951C117.257 44.9951 116.769 45.0922 116.313 45.2809C115.857 45.4696 115.444 45.7462 115.095 46.0949C114.746 46.4435 114.47 46.8574 114.281 47.313C114.092 47.7685 113.995 48.2568 113.995 48.7499C113.995 49.2429 114.092 49.7312 114.281 50.1867C114.47 50.6423 114.746 51.0562 115.095 51.4049L125.662 61.9649C122.946 65.7678 121.49 70.3265 121.5 74.9999V135C121.5 140.967 123.87 146.69 128.09 150.91C132.31 155.129 138.032 157.5 144 157.5H204C208.673 157.509 213.232 156.054 217.035 153.337L227.595 163.905C228.299 164.609 229.254 165.005 230.25 165.005C231.246 165.005 232.201 164.609 232.905 163.905C233.609 163.201 234.005 162.246 234.005 161.25C234.005 160.254 233.609 159.299 232.905 158.595L120.405 46.0949ZM169.605 105.907C168.306 106.459 167.123 107.251 166.117 108.24L131.092 142.65C129.721 140.334 128.998 137.691 129 135V74.9999C129 72.2249 129.757 69.6149 131.077 67.3799L169.605 105.907ZM144 150C141.225 150 138.637 149.25 136.41 147.945L171.375 113.595C172.076 112.908 173.018 112.523 174 112.523C174.981 112.523 175.924 112.908 176.625 113.595L211.59 147.945C209.288 149.293 206.668 150.002 204 150H144ZM219 74.9999V134.092L225.735 140.827C226.245 138.962 226.5 137.02 226.5 135V74.9999C226.5 69.0325 224.129 63.3095 219.91 59.09C215.69 54.8704 209.967 52.4999 204 52.4999H144C141.975 52.4999 140.032 52.7549 138.172 53.2649L144.9 59.9999H204C207.978 59.9999 211.793 61.5802 214.606 64.3933C217.42 67.2063 219 71.0216 219 74.9999ZM204 86.2499C204 84.7725 203.709 83.3096 203.144 81.9447C202.578 80.5798 201.749 79.3396 200.705 78.2949C199.66 77.2502 198.42 76.4216 197.055 75.8562C195.69 75.2908 194.227 74.9999 192.75 74.9999C191.272 74.9999 189.81 75.2908 188.445 75.8562C187.08 76.4216 185.84 77.2502 184.795 78.2949C183.75 79.3396 182.922 80.5798 182.356 81.9447C181.791 83.3096 181.5 84.7725 181.5 86.2499C181.5 89.2335 182.685 92.095 184.795 94.2048C186.905 96.3146 189.766 97.4999 192.75 97.4999C195.734 97.4999 198.595 96.3146 200.705 94.2048C202.815 92.095 204 89.2335 204 86.2499ZM196.5 86.2499C196.5 87.2444 196.105 88.1982 195.402 88.9015C194.698 89.6048 193.744 89.9999 192.75 89.9999C191.755 89.9999 190.801 89.6048 190.098 88.9015C189.395 88.1982 189 87.2444 189 86.2499C189 85.2553 189.395 84.3015 190.098 83.5982C190.801 82.8949 191.755 82.4999 192.75 82.4999C193.744 82.4999 194.698 82.8949 195.402 83.5982C196.105 84.3015 196.5 85.2553 196.5 86.2499Z' fill='url(%23paint0_linear_5310_528)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_5310_528' x1='174' y1='44.9951' x2='174' y2='165.005' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2364A696'/%3E%3Cstop offset='0.5' stop-color='%2364A696'/%3E%3Cstop offset='1' stop-color='%2364A696'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E"
                  alt="No logo"
                />
              )}
              <h3 className={styles.shopName}>
                {state.language === "en"
                  ? shop.nameEn || shop.nameRu
                  : shop.nameRu}
              </h3>
            </div>
          ))}
        </div>
      )}
      <RouteQRModal
        isOpen={isQRModalOpen}
        onClose={() => setIsQRModalOpen(false)}
        terminalName={
          new URLSearchParams(routerLocation.search).get("terminalName") ||
          state.terminalName ||
          null
        }
        shopId={state.selectedShop?.id}
      />
    </div>
  );
}
